import React, { useState } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import Select from "react-select";
import { Button } from '@app/components';
import { downloadStudentWeekSummary } from '@app/services/student';
import PrintWorksheetsPDF from './PrintWorksheetsPDF';
import { usePrintThresholdCheck } from '@app/hooks/usePrintThresholdCheck';
import { showThresholdExceededModal } from '@app/components/ThresholdExceededModal';

interface PrintDay {
    date: string;
    modules?: any[];
}

interface PrintWeek {
    id: number | string;
    name: string;
    week_number: number;
    days: PrintDay[];
}

interface PrintWorkActionsProps {
    studId: any;
    student: any;
    subjectId: number;      // 0 = Mix and Match
    weeks: PrintWeek[];     // available weeks with their day data
    canAccess: any;
    isLoading?: boolean;
}

/**
 * Shared print actions used on the Current Student Work and Work Summary History pages.
 * Renders the week multi-select with Print Worksheet(s), Print Handbooks and Print Week(s) Summary
 * actions, including the print threshold checks and the print PDF modals.
 */
const PrintWorkActions: React.FC<PrintWorkActionsProps> = ({ studId, student, subjectId, weeks, canAccess, isLoading = false }) => {

    const [selectedWeeks, setSelectedWeeks] = useState<(number | string)[]>([]);
    const [selectedDates, setSelectedDates] = useState<string[]>([]);
    const [weeksData, setWeeksData] = useState([]);
    const [weekError, setWeekError] = useState('');
    const [isWeekSummaryLoading, setWeekSummaryLoading] = useState(false);
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const [showHandbookPrint, setShowHandbookPrint] = useState(false);
    const handleCloseHandbookPrint = () => setShowHandbookPrint(false);
    const handleShowHandbookPrint = () => setShowHandbookPrint(true);
    const { loading: thresholdLoading, checkThreshold, checkMultipleSubjects } = usePrintThresholdCheck();

    const customStylesSelect = {
        dropdownIndicator: () => ({
            display: 'none',
        }),
    };

    const getWeek = (id: number | string): PrintWeek | undefined => weeks.find(w => w.id === id);

    const onSelectWeeks = (options: readonly any[]) => {
        setSelectedWeeks([].slice.call(options).map((option) => option.id));
    };

    const handlePrintWorksheets = async () => {
        if (selectedWeeks.length === 0) {
            setWeekError('Select week(s) to print the worksheets');
            return;
        }

        const sortedWeeks = [...selectedWeeks].sort();
        let dates: string[] = [];
        sortedWeeks.forEach((weekId) => {
            getWeek(weekId)?.days?.forEach((day) => dates.push(day.date));
        });
        setSelectedDates(dates);
        setWeekError('');

        // Only check threshold if local_printing_mode is worksheet_inclusive
        if (student?.local_printing_mode !== 'worksheet_inclusive') {
            handleShow();
            return;
        }

        // If the selected weeks have no per-day module data (e.g. weeks sourced from the
        // weeks-list which only carries dates), the worksheet counts can't be computed
        // client-side for the threshold check, so proceed straight to printing.
        const hasModuleData = sortedWeeks.some((weekId) =>
            getWeek(weekId)?.days?.some((d) => Array.isArray(d.modules) && d.modules.length > 0)
        );
        if (!hasModuleData) {
            handleShow();
            return;
        }

        // Mix and Match (subject_id = 0)
        if (subjectId == 0) {
            const subjectWorksheetMap = new Map<number, {
                subject_id: number;
                worksheet_count: number;
                dates: Set<string>;
            }>();

            sortedWeeks.forEach((weekId) => {
                getWeek(weekId)?.days?.forEach((dayObj) => {
                    if (dayObj?.modules && Array.isArray(dayObj.modules)) {
                        const dateFormatted = moment(dayObj.date).format('YYYY-MM-DD');
                        dayObj.modules.forEach((module) => {
                            const sid = module.subject_id;
                            if (!subjectWorksheetMap.has(sid)) {
                                subjectWorksheetMap.set(sid, { subject_id: sid, worksheet_count: 0, dates: new Set<string>() });
                            }
                            const subjectData = subjectWorksheetMap.get(sid)!;
                            subjectData.worksheet_count += 1;
                            subjectData.dates.add(dateFormatted);
                        });
                    }
                });
            });

            const subjectGroups = Array.from(subjectWorksheetMap.values()).map(subject => ({
                subject_id: subject.subject_id,
                worksheet_count: subject.worksheet_count,
                assignment_dates: Array.from(subject.dates) as string[]
            }));

            if (subjectGroups.length === 0) {
                toast.error('No worksheets found to print');
                return;
            }

            try {
                const exceededSubjects = await checkMultipleSubjects(
                    student.franchise_id,
                    student.id,
                    'setwork',
                    subjectGroups,
                    subjectId
                );
                if (exceededSubjects === null) {
                    // Threshold check failed - do not proceed to print (error already shown)
                    return;
                }
                if (exceededSubjects.length > 0) {
                    showThresholdExceededModal({ exceededSubjects, onConfirm: handleShow });
                } else {
                    handleShow();
                }
            } catch (error) {
                console.error('Threshold check failed:', error);
                toast.error('Failed to check printing threshold. Please try again.');
            }
        } else {
            // Single subject
            let totalWorksheets = 0;
            sortedWeeks.forEach((weekId) => {
                getWeek(weekId)?.days?.forEach((dayObj) => {
                    if (dayObj?.modules && Array.isArray(dayObj.modules)) {
                        totalWorksheets += dayObj.modules.length;
                    }
                });
            });

            const formattedDates = dates.map(date => moment(date).format('YYYY-MM-DD'));

            try {
                const payload = {
                    franchise_id: student.franchise_id,
                    student_id: student.id,
                    subject_id: subjectId,
                    type: 'setwork' as const,
                    worksheets_to_print: totalWorksheets,
                    assignment_dates: formattedDates,
                    work_subject_id: subjectId
                };

                const result = await checkThreshold(payload);

                if (!result) {
                    // Threshold check failed - do not proceed to print (error already shown)
                    return;
                }

                if (result.threshold_exceeded) {
                    showThresholdExceededModal({
                        exceededSubjects: [{
                            subject_name: result.subject_name || 'Unknown Subject',
                            worksheets_to_print: totalWorksheets,
                            threshold_exceeded: true,
                            month_type: result.month_type,
                            details: result.details,
                            month_details: result.month_details
                        }],
                        onConfirm: handleShow
                    });
                } else {
                    handleShow();
                }
            } catch (error) {
                console.error('Threshold check failed:', error);
                toast.error('Failed to check printing threshold. Please try again.');
            }
        }
    };

    const handlePrintHandbookSummary = () => {
        if (selectedWeeks.length === 0) {
            setWeekError('Select week(s) to print the handbook summary');
            return;
        }

        let dates: string[] = [];
        const constructedWeeksData = [...selectedWeeks].sort().map((weekId) => {
            const wk = getWeek(weekId);
            const weekDates = (wk?.days || []).map((day) => {
                dates.push(day.date);
                return day.date;
            });
            return { week_number: wk?.week_number, dates: weekDates };
        });
        setSelectedDates(dates);
        setWeeksData(constructedWeeksData as any);
        setWeekError('');
        handleShowHandbookPrint();
    };

    const handlePrintWeekSummary = async () => {
        try {
            if (selectedWeeks.length === 0) {
                setWeekError('Select week(s) to print the summary');
                return;
            }
            setWeekSummaryLoading(true);
            setWeekError('');

            const weeksDataPayload = [...selectedWeeks].sort().map((weekId) => {
                const wk = getWeek(weekId);
                return { week_number: wk?.week_number, dates: (wk?.days || []).map((day) => day.date) };
            });

            const dates = weeksDataPayload.flatMap(w => w.dates);
            setSelectedDates(dates);

            const payload = {
                student_id: studId,
                subject_id: subjectId,
                weeks: selectedWeeks.length,
                weeks_data: weeksDataPayload
            };

            const resp = await downloadStudentWeekSummary(payload);
            const fileURL = URL.createObjectURL(resp);
            window.open(fileURL, '_blank');
            setWeekSummaryLoading(false);
        } catch (error) {
            setWeekSummaryLoading(false);
            console.log(error);
        }
    };

    if (!(canAccess?.['print-worksheets'] || canAccess?.['print-handbooks'] || canAccess?.['print-weeks-summary'])) {
        return null;
    }

    return (
        <>
            <div className="row mb-4 bg-light p-3 rounded mx-0 align-items-center" style={{ border: '1px solid #dee2e6' }}>
                <div className="col-md-4 mb-2 mb-md-0">
                    <Select<PrintWeek, true>
                        isMulti
                        name="week"
                        placeholder="Select week(s) to print"
                        options={weeks}
                        getOptionValue={(option) => `${option['id']}`}
                        getOptionLabel={(option) => `${option['name']}`}
                        onChange={(e) => { onSelectWeeks(e) }}
                        value={weeks.filter((obj) => selectedWeeks.includes(obj.id))}
                        styles={customStylesSelect}
                        isLoading={isLoading}
                        isClearable={true}
                        closeMenuOnSelect={false}
                    />
                    <div className='text-danger'>{weekError}</div>
                </div>
                <div className="col-md-8 d-flex flex-wrap justify-content-md-end mt-2 mt-md-0">
                    {canAccess?.['print-worksheets'] && student?.student_local_printing && (
                        <Button type="button" isLoading={thresholdLoading} onClick={handlePrintWorksheets} className="btn btn-default mb-2" disabled={weeks.length == 0}><i className="fas fa-print"></i> Print Worksheet(s)</Button>
                    )}
                    {canAccess?.['print-handbooks'] && student?.enabled_handbook_print && (
                        <Button type="button" onClick={handlePrintHandbookSummary} className="btn btn-default ml-2 mb-2" disabled={weeks.length == 0}><i className="fas fa-print"></i> Print Handbooks</Button>
                    )}
                    {canAccess?.['print-weeks-summary'] && (
                        <Button type="button" isLoading={isWeekSummaryLoading} onClick={handlePrintWeekSummary} className="btn btn-default ml-2 mb-2" disabled={weeks.length == 0}><i className="fas fa-print"></i> Print Week(s) Summary</Button>
                    )}
                </div>
            </div>

            <PrintWorksheetsPDF
                isAdhoc={false}
                show={show}
                handleClose={handleClose}
                studId={studId}
                selectedDates={selectedDates}
                subjectId={subjectId}
                student={student}
                selectedWeeksCount={selectedWeeks.length}
            />

            <PrintWorksheetsPDF
                isAdhoc={false}
                isHandbookSummary={true}
                show={showHandbookPrint}
                handleClose={handleCloseHandbookPrint}
                studId={studId}
                selectedDates={selectedDates}
                subjectId={subjectId}
                student={student}
                selectedWeeksCount={selectedWeeks.length}
                weeksData={weeksData}
            />
        </>
    );
};

export default PrintWorkActions;
